import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import styles from './CodeOfConductPage.module.css';

const CHECKLIST_ITEMS = [
  { key: 'agreeRespect', text: 'Treat all colleagues, customers, vendors, and visitors with dignity, respect, and professionalism.' },
  { key: 'agreeProfessionalCommunication', text: 'Maintain professional conduct in all verbal, written, email, chat, and other workplace communications.' },
  { key: 'agreeFollowPolicies', text: 'Follow all Company policies, procedures, and instructions issued by management.' },
  { key: 'agreeDisciplineIntegrity', text: 'Maintain discipline, integrity, confidentiality, and ethical behaviour while performing my duties.' },
  { key: 'agreeNoHarassment', text: 'Refrain from any form of harassment, discrimination, bullying, intimidation, abusive language, or inappropriate behaviour.' },
  { key: 'agreeResponsibleUse', text: 'Use Company resources, systems, and information responsibly and only for authorised business purposes.' },
  { key: 'agreeReportMisconduct', text: 'Immediately report any misconduct, policy violation, harassment, or unethical behaviour to my Reporting Manager or the Human Resources Department.' },
  { key: 'agreeCooperateInvestigation', text: 'Cooperate with any investigation related to workplace misconduct or policy violations.' },
];

const formatDateTime = (dt) => dt
  ? new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const todayLabel = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const CodeOfConductPage = () => {
  const { user } = useAuth();
  const isHR = user?.isHR;

  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [checks, setChecks] = useState(
    Object.fromEntries(CHECKLIST_ITEMS.map((i) => [i.key, false]))
  );
  const [consentGiven, setConsentGiven] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const allChecked = CHECKLIST_ITEMS.every((i) => checks[i.key]) && consentGiven;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.getCodeOfConductStatus();
        if (res.success && res.data) {
          setSubmitted(!!res.data.submitted);
          setSubmittedAt(res.data.submittedAt || null);
        }
      } catch { }
      finally { setLoading(false); }
    })();
  }, []);

  const toggleCheck = (key) => setChecks((p) => ({ ...p, [key]: !p[key] }));

  const handleSubmit = async () => {
    setError('');
    if (!allChecked) {
      setError('Please read and tick every item, including the Employee Consent checkbox, before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.submitCodeOfConduct({ ...checks, consentGiven });
      if (res.success) {
        setSubmitted(true);
        setSubmittedAt(res.data?.submittedAt || new Date().toISOString());
      } else {
        setError(res.message || 'Failed to submit. Please try again.');
      }
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Code of Conduct &amp; Discipline Employee Acknowledgement</h1>
      </div>

      {submitted ? (
        <div className={styles.submittedCard}>
          <div className={styles.submittedIcon}>✅</div>
          <h2 className={styles.submittedTitle}>You have already submitted this acknowledgement</h2>
          <p className={styles.submittedDesc}>
            Submitted on <strong>{formatDateTime(submittedAt)}</strong>. No further action is needed.
          </p>
        </div>
      ) : (
        <div className={styles.card}>
          <h2 className={styles.formTitle}>Employee Guidelines: Workplace Norms, Code of Conduct &amp; Discipline</h2>
          <p className={styles.formSubtitle}>Online Employee Acknowledgement Form</p>
          <p className={styles.intro}>
            Please read the following guidelines carefully before submitting this acknowledgement.
          </p>

          <h3 className={styles.sectionTitle}>Workplace Norms &amp; Code of Conduct</h3>
          <p className={styles.sectionLead}>I understand and agree to:</p>
          <div className={styles.checklist}>
            {CHECKLIST_ITEMS.map((item) => (
              <label key={item.key} className={styles.checklistItem}>
                <input
                  type="checkbox"
                  checked={checks[item.key]}
                  onChange={() => toggleCheck(item.key)}
                />
                <span>{item.text}</span>
              </label>
            ))}
          </div>

          <h3 className={styles.sectionTitle}>Declaration</h3>
          <p className={styles.sectionLead}>I hereby confirm that:</p>
          <ul className={styles.declarationList}>
            <li>I have read and understood the Company's Workplace Norms, Code of Conduct, and Discipline Guidelines.</li>
            <li>I agree to comply with these guidelines at all times during my employment.</li>
            <li>
              I understand that any violation of these guidelines may result in disciplinary action,
              up to and including termination of employment, in accordance with Company policies and applicable laws.
            </li>
          </ul>
          <p className={styles.declarationNote}>
            By clicking "I Agree" and submitting this form, I acknowledge that this electronic declaration
            shall have the same validity as my physical signature.
          </p>

          <div className={styles.detailsGrid}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Employee Name</span>
              <span className={styles.detailValue}>{user?.name || '—'}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Employee ID</span>
              <span className={styles.detailValue}>{user?.employeeCode || '—'}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Department</span>
              <span className={styles.detailValue}>{user?.department || '—'}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Reporting Manager</span>
              <span className={styles.detailValue}>{user?.reportingManager || '—'}</span>
            </div>
          </div>

          <h3 className={styles.sectionTitle}>Employee Consent</h3>
          <label className={styles.checklistItem}>
            <input
              type="checkbox"
              checked={consentGiven}
              onChange={() => setConsentGiven((p) => !p)}
            />
            <span>
              I have read, understood, and agree to abide by the Company's Workplace Norms,
              Code of Conduct &amp; Discipline Policy.
            </span>
          </label>

          <div className={styles.detailItem} style={{ marginTop: 16 }}>
            <span className={styles.detailLabel}>Date</span>
            <span className={styles.detailValue}>{todayLabel}</span>
          </div>

          {error && <div className={styles.errorMsg}>{error}</div>}

          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!allChecked || submitting}
          >
            {submitting ? 'Submitting...' : 'I Agree & Submit'}
          </button>
        </div>
      )}

      {isHR && <HRReportSection />}
    </div>
  );
};

const HRReportSection = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending'); // 'pending' | 'submitted'
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.getCodeOfConductReport();
        if (res.success) setRows(res.data || []);
      } catch { }
      finally { setLoading(false); }
    })();
  }, []);

  const total = rows.length;
  const submittedCount = rows.filter((r) => r.submitted).length;
  const pendingCount = total - submittedCount;

  const filtered = useMemo(() => {
    return rows
      .filter((r) => (tab === 'pending' ? !r.submitted : r.submitted))
      .filter((r) =>
        !search ||
        r.employeeName?.toLowerCase().includes(search.toLowerCase()) ||
        r.employeeCode?.toLowerCase().includes(search.toLowerCase()) ||
        r.department?.toLowerCase().includes(search.toLowerCase())
      );
  }, [rows, tab, search]);

  return (
    <div className={styles.reportSection}>
      <div className={styles.reportHeader}>
        <h2 className={styles.reportTitle}>Acknowledgement Report</h2>
        <input
          className={styles.searchInput}
          placeholder="Search by name, code, or department..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--color-primary)' }}>{total}</div>
          <div className={styles.statLabel}>Total Employees</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--color-green)' }}>{submittedCount}</div>
          <div className={styles.statLabel}>Submitted</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: '#e53e3e' }}>{pendingCount}</div>
          <div className={styles.statLabel}>Pending</div>
        </div>
      </div>

      <div className={styles.tabRow}>
        <button
          className={`${styles.tabBtn} ${tab === 'pending' ? styles.tabActive : ''}`}
          onClick={() => setTab('pending')}
        >
          ⏳ Pending ({pendingCount})
        </button>
        <button
          className={`${styles.tabBtn} ${tab === 'submitted' ? styles.tabActive : ''}`}
          onClick={() => setTab('submitted')}
        >
          ✅ Submitted ({submittedCount})
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {loading ? (
          <div className={styles.loading}>Loading report...</div>
        ) : filtered.length === 0 ? (
          <div className={styles.emptyState}>No employees found.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Employee Code</th>
                <th>Department</th>
                <th>Reporting Manager</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, idx) => (
                <tr key={r.employeeId}>
                  <td style={{ color: 'var(--color-text-light)', fontSize: 12 }}>{idx + 1}</td>
                  <td><strong>{r.employeeName}</strong></td>
                  <td>{r.employeeCode || '—'}</td>
                  <td>{r.department || '—'}</td>
                  <td>{r.reportingManager || '—'}</td>
                  <td>
                    {r.submitted ? (
                      <span className={styles.statusFilled}>✅ {formatDateTime(r.submittedAt)}</span>
                    ) : (
                      <span className={styles.statusPending}>⏳ Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CodeOfConductPage;
